/**
 * Dashboard Configuration - Centralized per Role
 *
 * Defines what each user role sees in their dashboard.
 * Avoids hardcoding configuration in components for scalability.
 */

import type { UserRole } from './constants'

// =============================================================================
// Types
// =============================================================================

export interface DashboardChartConfig {
  /** Show modality distribution pie chart */
  showModalityChart: boolean
  /** Show productivity/timeline chart */
  showTimelineChart: boolean
  /** Show radiologist productivity table (admin dashboards) */
  showProductivityTable?: boolean
  /** Show TAT metrics (admin dashboards) */
  showTatMetrics?: boolean
  /** Use global endpoints instead of institution-filtered (SUPER_ADMIN) */
  useGlobalEndpoints?: boolean
  /** Custom title for modality chart */
  modalityTitle?: string
  /** Custom title for timeline chart */
  timelineTitle?: string
  /** Y-axis label for timeline */
  timelineYAxisLabel?: string
  /** Tooltip label for timeline data points */
  timelineTooltipLabel?: string
}

export interface DashboardStatsConfig {
  /** Fields to show in stats cards */
  fields: string[]
}

export interface DashboardRoleConfig {
  /** Welcome section configuration */
  welcome: {
    /** Subtitle shown below the greeting */
    subtitle: string
  }
  /** Charts configuration */
  charts: DashboardChartConfig
  /** Stats cards configuration (optional) */
  stats?: DashboardStatsConfig
}

// =============================================================================
// Configuration by Role
// =============================================================================

export const DASHBOARD_CONFIG: Record<UserRole, DashboardRoleConfig> = {
  RADIOLOGIST: {
    welcome: {
      subtitle: 'Tu lista de trabajo personal',
    },
    charts: {
      showModalityChart: true,
      showTimelineChart: true,
      modalityTitle: 'Mis Estudios Completados por Modalidad',
      timelineTitle: 'Mi Productividad - Estudios Leídos',
      timelineYAxisLabel: 'Estudios',
      timelineTooltipLabel: 'estudios completados',
    },
    stats: {
      fields: ['pendingCount', 'inProgressCount', 'completedToday', 'overdueCount'],
    },
  },

  TRANSCRIPTOR: {
    welcome: {
      subtitle: 'Cola de transcripción',
    },
    charts: {
      showModalityChart: true,
      showTimelineChart: true,
      modalityTitle: 'Mis Transcripciones por Modalidad',
      timelineTitle: 'Mi Productividad - Transcripciones',
      timelineYAxisLabel: 'Transcripciones',
      timelineTooltipLabel: 'reportes transcritos',
    },
    stats: {
      fields: ['pendingQueue', 'inProgressCount', 'transcribedToday'],
    },
  },

  TECHNICIAN: {
    welcome: {
      subtitle: 'Panel de técnico',
    },
    charts: {
      showModalityChart: true,
      showTimelineChart: true,
      modalityTitle: 'Mis Estudios Subidos por Modalidad',
      timelineTitle: 'Mi Productividad - Estudios Subidos',
      timelineYAxisLabel: 'Estudios',
      timelineTooltipLabel: 'estudios subidos',
    },
    stats: {
      fields: ['uploadedToday', 'assignedToday', 'unassignedCount', 'returnedCount'],
    },
  },

  DOCTOR: {
    welcome: {
      subtitle: 'Portal médico',
    },
    charts: {
      showModalityChart: false,
      showTimelineChart: false,
    },
    stats: {
      fields: ['patientsTotal', 'studiesRequested', 'reportsAvailable'],
    },
  },

  ADMIN: {
    welcome: {
      subtitle: 'Panel de administración - Tu institución',
    },
    charts: {
      showModalityChart: true,
      showTimelineChart: true,
      showProductivityTable: true,
      showTatMetrics: true,
      modalityTitle: 'Estudios por Modalidad',
      timelineTitle: 'Estudios en el Tiempo',
      timelineYAxisLabel: 'Estudios',
      timelineTooltipLabel: 'estudios',
    },
  },

  SUPER_ADMIN: {
    welcome: {
      subtitle: 'Panel global - Todas las instituciones',
    },
    charts: {
      showModalityChart: true,
      showTimelineChart: true,
      showProductivityTable: true,
      showTatMetrics: true,
      useGlobalEndpoints: true,
      modalityTitle: 'Distribución Global por Modalidad',
      timelineTitle: 'Estudios Globales en el Tiempo',
      timelineYAxisLabel: 'Estudios',
      timelineTooltipLabel: 'estudios totales',
    },
  },

  PATIENT: {
    welcome: {
      subtitle: 'Portal del paciente',
    },
    charts: {
      showModalityChart: false,
      showTimelineChart: false,
    },
  },
}

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Get dashboard configuration for a specific role
 * Returns PATIENT config as fallback for unknown roles
 */
export function getDashboardConfig(role: UserRole | string | undefined): DashboardRoleConfig {
  if (!role || !(role in DASHBOARD_CONFIG)) {
    return DASHBOARD_CONFIG.PATIENT
  }
  return DASHBOARD_CONFIG[role as UserRole]
}

/**
 * Check if a role should see personal charts (modality and/or timeline)
 */
export function shouldShowPersonalCharts(role: UserRole | string | undefined): boolean {
  const config = getDashboardConfig(role)
  return config.charts.showModalityChart || config.charts.showTimelineChart
}

/**
 * Check if a role should use global endpoints (SUPER_ADMIN only)
 */
export function shouldUseGlobalEndpoints(role: UserRole | string | undefined): boolean {
  const config = getDashboardConfig(role)
  return config.charts.useGlobalEndpoints === true
}

/**
 * Get the appropriate chart title based on role
 */
export function getModalityChartTitle(role: UserRole | string | undefined): string {
  const config = getDashboardConfig(role)
  return config.charts.modalityTitle || 'Distribución por Modalidad'
}

/**
 * Get the appropriate timeline chart title based on role
 */
export function getTimelineChartTitle(role: UserRole | string | undefined): string {
  const config = getDashboardConfig(role)
  return config.charts.timelineTitle || 'Actividad en el Tiempo'
}

/**
 * Roles that can see personal productivity charts
 */
export const ROLES_WITH_PERSONAL_CHARTS: UserRole[] = [
  'RADIOLOGIST',
  'TRANSCRIPTOR',
  'TECHNICIAN',
]

/**
 * Roles that can see admin-level charts (institution or global)
 */
export const ROLES_WITH_ADMIN_CHARTS: UserRole[] = [
  'ADMIN',
  'SUPER_ADMIN',
]
